import { AI_SKILLS, AI_SKILL_NAMES, type AISkillName } from '@polyloop/sim';
import { TRACKS, TRACK_IDS, type TrackId } from '../engine/tracks.js';

export interface TitleSelection {
  aiCount: number; // 0 = solo, 1..3 = with N AI opponents
  aiSkill: AISkillName;
  trackId: TrackId;
}

/**
 * Boot screen. Pick mode + AI count + difficulty, then "DRIVE."
 * Vanilla DOM (no React yet); replaced by `@polyloop/ui-kit` in a later phase.
 */
export class TitleScreen {
  private root: HTMLDivElement;
  private resolver: ((sel: TitleSelection) => void) | null = null;
  private selection: TitleSelection = { aiCount: 2, aiSkill: 'rookie', trackId: 'sandbox' };

  constructor() {
    this.root = document.createElement('div');
    this.root.className = 'title-overlay';
    this.root.innerHTML = `
      <div class="title-inner">
        <h1 class="title-logo">POLYLOOP</h1>
        <p class="title-tagline">Low-poly stunt racing. Web-native. Built for speed.</p>

        <div class="title-section">
          <label class="title-label">TRACK</label>
          <div class="title-pills" data-group="track">
            ${TRACK_IDS.map(
              (id) =>
                `<button data-v="${id}" class="title-pill track ${id === 'sandbox' ? 'is-on' : ''}">${TRACKS[id]!.name.toUpperCase()}</button>`,
            ).join('')}
          </div>
          <p class="title-hint" data-track-hint>${TRACKS.sandbox!.blurb}</p>
        </div>

        <div class="title-section">
          <label class="title-label">OPPONENTS</label>
          <div class="title-pills" data-group="count">
            <button data-v="0" class="title-pill">SOLO</button>
            <button data-v="1" class="title-pill">1 AI</button>
            <button data-v="2" class="title-pill is-on">2 AI</button>
            <button data-v="3" class="title-pill">3 AI</button>
          </div>
        </div>

        <div class="title-section" data-skill-section>
          <label class="title-label">DIFFICULTY</label>
          <div class="title-pills" data-group="skill">
            ${AI_SKILL_NAMES.map(
              (n) =>
                `<button data-v="${n}" class="title-pill skill ${n === 'rookie' ? 'is-on' : ''}">${AI_SKILLS[n]!.name.toUpperCase()}</button>`,
            ).join('')}
          </div>
          <p class="title-hint" data-skill-hint>${describeSkill('rookie')}</p>
        </div>

        <button class="title-drive" data-drive>DRIVE</button>

        <div class="title-howto">
          <div class="howto-col">
            <h3 class="howto-title">CONTROLS — ON GROUND</h3>
            <ul class="howto-list">
              <li><span class="key">W / ↑</span><span class="desc">throttle</span></li>
              <li><span class="key">S / ↓</span><span class="desc">brake / reverse</span></li>
              <li><span class="key">A · D</span><span class="desc">steer</span></li>
              <li><span class="key">SHIFT</span><span class="desc">drift</span></li>
              <li><span class="key">SPACE</span><span class="desc">boost</span></li>
              <li><span class="key">R</span><span class="desc">respawn</span></li>
            </ul>
          </div>
          <div class="howto-col">
            <h3 class="howto-title">CONTROLS — IN AIR</h3>
            <ul class="howto-list">
              <li><span class="key">W</span><span class="desc">front flip (pitch nose down)</span></li>
              <li><span class="key">S</span><span class="desc">back flip (pitch nose up)</span></li>
              <li><span class="key">A · D</span><span class="desc">barrel roll</span></li>
              <li class="hint">Hit any ramp to launch — full rotation in the air.</li>
            </ul>
          </div>
          <div class="howto-col howto-col-wide">
            <h3 class="howto-title">HOW TO SCORE</h3>
            <ul class="howto-list">
              <li><span class="pts">+5/s</span><span class="desc">airtime — every second you stay airborne</span></li>
              <li><span class="pts">+25</span><span class="desc">AIR — landing after >0.6s of air</span></li>
              <li><span class="pts">+200</span><span class="desc">LONG JUMP — landing after >1.5s of air</span></li>
              <li><span class="pts">+500</span><span class="desc">FLIP — go upside-down then land upright</span></li>
              <li><span class="pts">+1/100ms</span><span class="desc">DRIFT — hold SHIFT while turning at speed</span></li>
              <li><span class="pts">+50</span><span class="desc">BOOST PAD — drive over a yellow strip</span></li>
              <li><span class="pts">+25</span><span class="desc">BUMP — knock another car off-line</span></li>
            </ul>
          </div>
        </div>

        <p class="title-credit">v0.2.0 · flips · scores · 3 tracks · AI tricks</p>
      </div>
    `;
    document.body.appendChild(this.root);
    injectTitleStyles();
    this.bind();
  }

  private bind(): void {
    const onPill = (group: 'count' | 'skill' | 'track') => (e: Event) => {
      const btn = (e.target as HTMLElement).closest('button.title-pill');
      if (!btn) return;
      const groupEl = this.root.querySelector(`[data-group="${group}"]`);
      groupEl?.querySelectorAll('.title-pill').forEach((b) => b.classList.remove('is-on'));
      btn.classList.add('is-on');
      if (group === 'count') {
        this.selection.aiCount = Number(btn.getAttribute('data-v') ?? 0);
        this.updateSkillVisibility();
      } else if (group === 'skill') {
        this.selection.aiSkill = (btn.getAttribute('data-v') ?? 'rookie') as AISkillName;
        const hint = this.root.querySelector('[data-skill-hint]');
        if (hint) hint.textContent = describeSkill(this.selection.aiSkill);
      } else if (group === 'track') {
        this.selection.trackId = (btn.getAttribute('data-v') ?? 'sandbox') as TrackId;
        const hint = this.root.querySelector('[data-track-hint]');
        if (hint) hint.textContent = TRACKS[this.selection.trackId]!.blurb;
      }
    };

    this.root.querySelector('[data-group="track"]')?.addEventListener('click', onPill('track'));
    this.root.querySelector('[data-group="count"]')?.addEventListener('click', onPill('count'));
    this.root.querySelector('[data-group="skill"]')?.addEventListener('click', onPill('skill'));

    const drive = this.root.querySelector<HTMLButtonElement>('[data-drive]');
    drive?.addEventListener('click', () => this.resolve());

    // Enter / Space starts the game.
    const onKey = (e: KeyboardEvent) => {
      if (e.code === 'Enter' || e.code === 'Space') {
        e.preventDefault();
        this.resolve();
      }
    };
    document.addEventListener('keydown', onKey);
    this.cleanupKey = () => document.removeEventListener('keydown', onKey);

    this.updateSkillVisibility();
  }

  private cleanupKey: () => void = () => undefined;

  private updateSkillVisibility(): void {
    const section = this.root.querySelector<HTMLElement>('[data-skill-section]');
    if (!section) return;
    section.style.opacity = this.selection.aiCount === 0 ? '0.35' : '1';
    section.style.pointerEvents = this.selection.aiCount === 0 ? 'none' : 'auto';
  }

  show(): Promise<TitleSelection> {
    return new Promise((resolve) => {
      this.resolver = resolve;
      // Fade in.
      requestAnimationFrame(() => this.root.classList.add('is-visible'));
    });
  }

  private resolve(): void {
    if (!this.resolver) return;
    const sel = { ...this.selection };
    const r = this.resolver;
    this.resolver = null;
    this.root.classList.add('is-leaving');
    this.cleanupKey();
    setTimeout(() => {
      this.root.remove();
      r(sel);
    }, 320);
  }
}

function describeSkill(name: AISkillName): string {
  switch (name) {
    case 'trainee':
      return 'Slow, wandery, won\'t use boost. Good for warming up.';
    case 'rookie':
      return 'Decent pace, brakes for corners, light boost use.';
    case 'ace':
      return 'Tight racing line, drifts on tight turns, boosts on straights.';
    case 'champion':
      return 'No mistakes. Optimal line. Boosts wherever possible. Brings the heat.';
  }
}

let stylesInjected = false;
function injectTitleStyles(): void {
  if (stylesInjected) return;
  stylesInjected = true;
  const css = `
    .title-overlay {
      position: fixed;
      inset: 0;
      background: radial-gradient(circle at 50% 30%, #1a1d35, #0c0d14 65%);
      z-index: 90;
      opacity: 0;
      transition: opacity 280ms ease;
      overflow-x: hidden;
      overflow-y: auto;
    }
    .title-overlay.is-visible { opacity: 1; }
    .title-overlay.is-leaving { opacity: 0; transform: scale(0.985); transition: opacity 280ms ease, transform 280ms ease; }
    .title-inner {
      max-width: 540px;
      margin: 0 auto;
      padding: 8vh 16px 32px;
      text-align: center;
      color: #f3f4ff;
      box-sizing: border-box;
    }
    @media (max-width: 480px) {
      .title-inner { padding: 4vh 12px 24px; }
    }
    .title-logo {
      font-size: clamp(1.9rem, 9vw, 5.2rem);
      letter-spacing: 0.06em;
      font-weight: 900;
      line-height: 1;
      margin-bottom: 8px;
      background: linear-gradient(90deg, #00e0ff, #ff3ea5 70%, #ffd75c);
      -webkit-background-clip: text;
      background-clip: text;
      color: transparent;
      filter: drop-shadow(0 6px 30px rgba(0, 224, 255, 0.18));
    }
    @media (min-width: 600px) {
      .title-logo { letter-spacing: 0.18em; }
    }
    .title-tagline {
      color: rgba(255, 255, 255, 0.55);
      font-size: 0.95rem;
      letter-spacing: 0.05em;
      margin-bottom: 32px;
    }
    .title-section { margin-bottom: 22px; transition: opacity 200ms ease; }
    .title-label {
      display: block;
      font-size: 0.75rem;
      letter-spacing: 0.22em;
      color: rgba(255, 255, 255, 0.45);
      margin-bottom: 10px;
      font-weight: 600;
    }
    .title-pills {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      justify-content: center;
    }
    .title-pill {
      flex: 1 1 72px;
      min-width: 64px;
      padding: 10px 10px;
      border-radius: 10px;
      background: rgba(255, 255, 255, 0.04);
      border: 1px solid rgba(255, 255, 255, 0.12);
      color: rgba(255, 255, 255, 0.78);
      font-weight: 700;
      letter-spacing: 0.08em;
      font-size: 0.82rem;
      cursor: pointer;
      transition: all 140ms ease;
      font-family: inherit;
    }
    .title-pill:hover { background: rgba(255, 255, 255, 0.08); border-color: rgba(255, 255, 255, 0.25); }
    .title-pill.is-on {
      background: linear-gradient(135deg, rgba(0, 224, 255, 0.18), rgba(255, 62, 165, 0.18));
      border-color: rgba(0, 224, 255, 0.7);
      color: #fff;
      box-shadow: 0 4px 16px rgba(0, 224, 255, 0.18);
    }
    .title-pill.skill.is-on[data-v="trainee"]  { border-color: rgba(160, 200, 220, 0.7); background: linear-gradient(135deg, rgba(160, 200, 220, 0.18), rgba(255, 255, 255, 0.05)); }
    .title-pill.skill.is-on[data-v="rookie"]   { border-color: rgba(80, 160, 255, 0.7); background: linear-gradient(135deg, rgba(80, 160, 255, 0.18), rgba(80, 160, 255, 0.05)); }
    .title-pill.skill.is-on[data-v="ace"]      { border-color: rgba(255, 160, 70, 0.7); background: linear-gradient(135deg, rgba(255, 160, 70, 0.18), rgba(255, 80, 70, 0.05)); }
    .title-pill.skill.is-on[data-v="champion"] { border-color: rgba(255, 215, 92, 0.85); background: linear-gradient(135deg, rgba(255, 215, 92, 0.22), rgba(255, 62, 165, 0.1)); }

    .title-hint {
      margin-top: 10px;
      color: rgba(255, 255, 255, 0.55);
      font-size: 0.84rem;
      min-height: 1.2em;
    }

    .title-drive {
      margin-top: 18px;
      width: 100%;
      padding: 18px 24px;
      font-size: 1.15rem;
      font-weight: 900;
      letter-spacing: 0.3em;
      color: #0c0d14;
      background: linear-gradient(90deg, #00e0ff, #ff3ea5);
      border: none;
      border-radius: 12px;
      cursor: pointer;
      transition: transform 120ms ease, box-shadow 180ms ease;
      box-shadow: 0 8px 28px rgba(0, 224, 255, 0.28), 0 12px 40px rgba(255, 62, 165, 0.2);
      font-family: inherit;
    }
    .title-drive:hover { transform: translateY(-1px) scale(1.005); box-shadow: 0 12px 36px rgba(0, 224, 255, 0.4), 0 16px 48px rgba(255, 62, 165, 0.28); }
    .title-drive:active { transform: translateY(0) scale(0.998); }

    .title-howto {
      margin-top: 28px;
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 16px;
      text-align: left;
    }
    @media (max-width: 600px) {
      .title-howto { grid-template-columns: 1fr; }
    }
    .howto-col {
      background: rgba(255, 255, 255, 0.025);
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 12px;
      padding: 14px 16px;
    }
    .howto-col-wide {
      grid-column: 1 / -1;
    }
    .howto-title {
      font-size: 0.72rem;
      letter-spacing: 0.22em;
      color: #00e0ff;
      font-weight: 700;
      margin-bottom: 10px;
    }
    .howto-list {
      list-style: none;
      padding: 0;
      margin: 0;
    }
    .howto-list li {
      display: flex;
      align-items: baseline;
      gap: 12px;
      padding: 4px 0;
      font-size: 0.84rem;
      color: rgba(255, 255, 255, 0.78);
    }
    .howto-list .key {
      display: inline-block;
      min-width: 80px;
      font-family: ui-monospace, SFMono-Regular, 'Menlo', monospace;
      font-weight: 700;
      color: #fff;
      background: rgba(255, 255, 255, 0.06);
      border: 1px solid rgba(255, 255, 255, 0.12);
      padding: 2px 8px;
      border-radius: 6px;
      font-size: 0.78rem;
      letter-spacing: 0.04em;
      text-align: center;
    }
    .howto-list .pts {
      display: inline-block;
      min-width: 80px;
      font-weight: 800;
      color: #ffd75c;
      font-feature-settings: 'tnum' 1;
      font-size: 0.85rem;
      letter-spacing: 0.04em;
    }
    .howto-list .desc {
      color: rgba(255, 255, 255, 0.72);
      line-height: 1.35;
      flex: 1;
    }
    .howto-list .hint {
      color: rgba(255, 255, 255, 0.45);
      font-style: italic;
      padding-top: 6px;
      font-size: 0.78rem;
    }
    .title-credit {
      margin-top: 14px;
      color: rgba(255, 255, 255, 0.25);
      font-size: 0.72rem;
      letter-spacing: 0.18em;
      text-align: center;
    }
  `;
  const style = document.createElement('style');
  style.textContent = css;
  document.head.appendChild(style);
}
